/**
 * The shared frame for apps/web's prose pages: page chrome, the plain-words toggle, and
 * the footer nav.
 *
 * The register toggle is on these pages for the same reason it is on the marketing page
 * (product-concept §11): a privacy policy is the single document a literal-minded reader
 * most needs in literal register, and offering the metaphor register everywhere except
 * the page about their data would be the wrong page to make an exception of.
 */
import type { ReactNode } from 'react';
import { useCopy } from '../theme/CopyProvider';
import { useTheme } from '../theme/ThemeProvider';
import { LiteralToggle } from '../marketing/LiteralToggle';
import { SiteFooter, type SitePath } from './SiteFooter';
import { text } from './prose';
import type { CopyKey } from '@carn/copy';

export function PageFrame({
  current,
  titleKey,
  children,
}: {
  current: SitePath;
  titleKey: CopyKey;
  children: ReactNode;
}) {
  const theme = useTheme();
  const { t } = useCopy();
  const c = theme.colours;
  const s = theme.space;

  return (
    <main
      style={{
        minHeight: '100vh',
        background: c.background,
        color: c.text,
        fontFamily: theme.type.family,
      }}
    >
      <div
        style={{
          maxWidth: 640,
          margin: '0 auto',
          padding: s.screenPadding,
          display: 'flex',
          flexDirection: 'column',
          gap: s.xxl,
        }}
      >
        <header style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <LiteralToggle />
        </header>

        <h1
          style={{
            ...text(theme.type.title),
            fontSize: theme.type.title.fontSize * 1.3,
            color: c.text,
            margin: 0,
          }}
        >
          {t(titleKey)}
        </h1>

        {children}

        <footer style={{ paddingBottom: s.xxl }}>
          <SiteFooter current={current} />
        </footer>
      </div>
    </main>
  );
}

/**
 * A titled run of paragraphs.
 *
 * Prose, not cards: the marketing page's list-of-claims presentation is right for four
 * one-line commitments and wrong for a policy somebody reads top to bottom. Headings are
 * real `h2`s so the page has an outline a screen reader can jump through — which is how
 * this audience will actually read it.
 */
export function Section({
  titleKey,
  bodyKeys,
  values,
}: {
  titleKey: CopyKey;
  bodyKeys: readonly CopyKey[];
  /** Slot values, applied to every body key that declares one. */
  values?: Record<string, string | number>;
}) {
  const theme = useTheme();
  const { t } = useCopy();
  const c = theme.colours;
  const s = theme.space;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: s.sm }}>
      <h2
        style={{
          ...text(theme.type.title),
          fontSize: theme.type.title.fontSize * 0.7,
          color: c.text,
          margin: 0,
        }}
      >
        {t(titleKey)}
      </h2>
      {bodyKeys.map((key) => (
        <p key={key} style={{ ...text(theme.type.body), color: c.textSecondary, margin: 0 }}>
          {t(key, values)}
        </p>
      ))}
    </section>
  );
}
